import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ShieldCheck,
  Download,
  FileText,
  Search,
  Activity,
  Users,
  AlertOctagon,
  Clock,
} from 'lucide-react';
import api from '@/lib/api';
import PageHeader from '@/components/ui/PageHeader';
import DataTable, { Column } from '@/components/ui/DataTable';
import SideDrawer from '@/components/ui/SideDrawer';
import { useTableExport } from '@/hooks/useTableExport';
import {
  format,
  subMonths,
  subDays,
  subHours,
  subMinutes,
} from 'date-fns';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface AuditEntry extends Record<string, unknown> {
  id: string;
  timestamp: string;
  user: string;
  role: string;
  module: string;
  action: 'CREATE' | 'UPDATE' | 'DELETE' | 'LOGIN' | 'LOGOUT' | 'EXPORT' | 'APPROVE' | 'REJECT' | 'REVIEW';
  entityCode: string;
  entityDescription: string;
  previousValue?: string;
  newValue?: string;
  ipAddress: string;
  sessionId: string;
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

const NOW = new Date();

function ts(offsetFn: (d: Date) => Date): string {
  return offsetFn(NOW).toISOString();
}

function sid(): string {
  return 'SID-' + Math.random().toString(36).slice(2, 10).toUpperCase();
}

// ─── Mock Data ─────────────────────────────────────────────────────────────────

const MOCK_AUDIT: AuditEntry[] = [
  // ── CAPA ──────────────────────────────────────────────────────────────────
  {
    id: 'a001', timestamp: ts(d => subMonths(d, 2)),
    user: 'Dr. Kavita Lal', role: 'QA Manager',
    module: 'CAPA', action: 'APPROVE',
    entityCode: 'CAPA-2024-015',
    entityDescription: 'CAPA-2024-015 — OOS Dissolution Failure',
    ipAddress: '10.0.1.42', sessionId: sid(),
  },
  {
    id: 'a002', timestamp: ts(d => subMonths(d, 2)),
    user: 'Ramesh Kulkarni', role: 'QC Analyst',
    module: 'CAPA', action: 'CREATE',
    entityCode: 'CAPA-2024-015',
    entityDescription: 'Opened CAPA for OOS Dissolution Failure on Ibuprofen 400mg',
    ipAddress: '10.0.1.55', sessionId: sid(),
  },
  {
    id: 'a003', timestamp: ts(d => subDays(d, 45)),
    user: 'Dr. Anjali Mehta', role: 'QA Head',
    module: 'CAPA', action: 'REVIEW',
    entityCode: 'CAPA-2024-018',
    entityDescription: 'CAPA-2024-018 — Cleaning Validation Gap',
    ipAddress: '10.0.2.11', sessionId: sid(),
  },
  {
    id: 'a004', timestamp: ts(d => subDays(d, 38)),
    user: 'Priya Sharma', role: 'QC Senior Analyst',
    module: 'CAPA', action: 'UPDATE',
    entityCode: 'CAPA-2024-018',
    entityDescription: 'Updated action plan steps for CAPA-2024-018',
    previousValue: 'Step 3: Retrain staff (due 2024-10-01)',
    newValue: 'Step 3: Retrain staff + verification (due 2024-09-20)',
    ipAddress: '10.0.1.77', sessionId: sid(),
  },
  {
    id: 'a005', timestamp: ts(d => subDays(d, 10)),
    user: 'Dr. Kavita Lal', role: 'QA Manager',
    module: 'CAPA', action: 'REJECT',
    entityCode: 'CAPA-2024-022',
    entityDescription: 'CAPA-2024-022 — Rejected: insufficient root cause evidence',
    ipAddress: '10.0.1.42', sessionId: sid(),
  },

  // ── SOP ───────────────────────────────────────────────────────────────────
  {
    id: 'a006', timestamp: ts(d => subMonths(d, 1)),
    user: 'Harish Reddy', role: 'Document Controller',
    module: 'SOP', action: 'UPDATE',
    entityCode: 'SOP-QC-101',
    entityDescription: 'SOP-QC-101 version updated from 2.0 to 2.1',
    previousValue: 'Version: 2.0',
    newValue: 'Version: 2.1',
    ipAddress: '10.0.1.34', sessionId: sid(),
  },
  {
    id: 'a007', timestamp: ts(d => subDays(d, 55)),
    user: 'Dr. Anjali Mehta', role: 'QA Head',
    module: 'SOP', action: 'APPROVE',
    entityCode: 'SOP-QC-101',
    entityDescription: 'Approved SOP-QC-101 v2.1 — HPLC Method Validation',
    ipAddress: '10.0.2.11', sessionId: sid(),
  },
  {
    id: 'a008', timestamp: ts(d => subDays(d, 70)),
    user: 'Harish Reddy', role: 'Document Controller',
    module: 'SOP', action: 'CREATE',
    entityCode: 'SOP-MFG-045',
    entityDescription: 'New SOP created: SOP-MFG-045 Granulation Process Control',
    ipAddress: '10.0.1.34', sessionId: sid(),
  },
  {
    id: 'a009', timestamp: ts(d => subDays(d, 22)),
    user: 'Priya Sharma', role: 'QC Senior Analyst',
    module: 'SOP', action: 'REVIEW',
    entityCode: 'SOP-QC-088',
    entityDescription: 'Periodic review of SOP-QC-088 — Dissolution Testing',
    ipAddress: '10.0.1.77', sessionId: sid(),
  },
  {
    id: 'a010', timestamp: ts(d => subDays(d, 5)),
    user: 'Meena Iyer', role: 'Regulatory Affairs',
    module: 'SOP', action: 'EXPORT',
    entityCode: 'SOP-RA-012',
    entityDescription: 'Exported SOP-RA-012 for CDSCO submission package',
    ipAddress: '10.0.3.20', sessionId: sid(),
  },

  // ── Batch Record ──────────────────────────────────────────────────────────
  {
    id: 'a011', timestamp: ts(d => subMonths(d, 3)),
    user: 'System', role: 'Automated',
    module: 'Batch Record', action: 'CREATE',
    entityCode: 'BT-AMX-24001',
    entityDescription: 'Batch BT-AMX-24001 record created — Amoxicillin 500mg',
    ipAddress: '127.0.0.1', sessionId: sid(),
  },
  {
    id: 'a012', timestamp: ts(d => subMonths(d, 3)),
    user: 'Arun Menon', role: 'Production Supervisor',
    module: 'Batch Record', action: 'UPDATE',
    entityCode: 'BT-AMX-24001',
    entityDescription: 'Updated yield entry for BT-AMX-24001 granulation step',
    previousValue: 'Yield: 98.2 kg',
    newValue: 'Yield: 97.8 kg (corrected after reweigh)',
    ipAddress: '10.0.4.12', sessionId: sid(),
  },
  {
    id: 'a013', timestamp: ts(d => subDays(d, 60)),
    user: 'Dr. Kavita Lal', role: 'QA Manager',
    module: 'Batch Record', action: 'APPROVE',
    entityCode: 'BT-IBU-24005',
    entityDescription: 'Batch record BT-IBU-24005 approved for release',
    ipAddress: '10.0.1.42', sessionId: sid(),
  },
  {
    id: 'a014', timestamp: ts(d => subDays(d, 30)),
    user: 'Arun Menon', role: 'Production Supervisor',
    module: 'Batch Record', action: 'CREATE',
    entityCode: 'BT-MET-24012',
    entityDescription: 'Batch record BT-MET-24012 opened — Metformin 850mg',
    ipAddress: '10.0.4.12', sessionId: sid(),
  },
  {
    id: 'a015', timestamp: ts(d => subDays(d, 14)),
    user: 'Ramesh Kulkarni', role: 'QC Analyst',
    module: 'Batch Record', action: 'EXPORT',
    entityCode: 'BT-SAL-24009',
    entityDescription: 'Exported batch record BT-SAL-24009 for audit',
    ipAddress: '10.0.1.55', sessionId: sid(),
  },

  // ── Sample ────────────────────────────────────────────────────────────────
  {
    id: 'a016', timestamp: ts(d => subHours(d, 3)),
    user: 'Priya Sharma', role: 'QC Senior Analyst',
    module: 'Sample', action: 'CREATE',
    entityCode: 'SMP-2024-0988',
    entityDescription: 'New sample SMP-2024-0988 received — Stability T=6M',
    ipAddress: '10.0.1.77', sessionId: sid(),
  },
  {
    id: 'a017', timestamp: ts(d => subHours(d, 6)),
    user: 'Anita Rao', role: 'Lab Analyst',
    module: 'Sample', action: 'UPDATE',
    entityCode: 'SMP-2024-0975',
    entityDescription: 'Updated test status for SMP-2024-0975',
    previousValue: 'Status: In Testing',
    newValue: 'Status: Results Pending Review',
    ipAddress: '10.0.1.90', sessionId: sid(),
  },
  {
    id: 'a018', timestamp: ts(d => subDays(d, 3)),
    user: 'Ramesh Kulkarni', role: 'QC Analyst',
    module: 'Sample', action: 'DELETE',
    entityCode: 'SMP-2024-0901',
    entityDescription: 'Duplicate sample SMP-2024-0901 removed after verification',
    ipAddress: '10.0.1.55', sessionId: sid(),
  },
  {
    id: 'a019', timestamp: ts(d => subDays(d, 7)),
    user: 'Anita Rao', role: 'Lab Analyst',
    module: 'Sample', action: 'APPROVE',
    entityCode: 'SMP-2024-0960',
    entityDescription: 'Sample SMP-2024-0960 results approved and locked',
    ipAddress: '10.0.1.90', sessionId: sid(),
  },

  // ── Document ──────────────────────────────────────────────────────────────
  {
    id: 'a020', timestamp: ts(d => subDays(d, 80)),
    user: 'Harish Reddy', role: 'Document Controller',
    module: 'Document', action: 'CREATE',
    entityCode: 'DOC-VAL-2024-007',
    entityDescription: 'Validation master plan DOC-VAL-2024-007 created',
    ipAddress: '10.0.1.34', sessionId: sid(),
  },
  {
    id: 'a021', timestamp: ts(d => subDays(d, 75)),
    user: 'Dr. Anjali Mehta', role: 'QA Head',
    module: 'Document', action: 'APPROVE',
    entityCode: 'DOC-VAL-2024-007',
    entityDescription: 'Approved Validation Master Plan DOC-VAL-2024-007',
    ipAddress: '10.0.2.11', sessionId: sid(),
  },
  {
    id: 'a022', timestamp: ts(d => subDays(d, 18)),
    user: 'Meena Iyer', role: 'Regulatory Affairs',
    module: 'Document', action: 'EXPORT',
    entityCode: 'DOC-RA-2024-011',
    entityDescription: 'Exported regulatory dossier DOC-RA-2024-011',
    ipAddress: '10.0.3.20', sessionId: sid(),
  },
  {
    id: 'a023', timestamp: ts(d => subDays(d, 12)),
    user: 'Harish Reddy', role: 'Document Controller',
    module: 'Document', action: 'UPDATE',
    entityCode: 'DOC-SOP-2024-033',
    entityDescription: 'Revised effective date for DOC-SOP-2024-033',
    previousValue: 'Effective: 2024-12-01',
    newValue: 'Effective: 2024-11-15',
    ipAddress: '10.0.1.34', sessionId: sid(),
  },

  // ── Certification ─────────────────────────────────────────────────────────
  {
    id: 'a024', timestamp: ts(d => subMonths(d, 2)),
    user: 'Dr. Anjali Mehta', role: 'QA Head',
    module: 'Certification', action: 'APPROVE',
    entityCode: 'CERT-GMP-2024-04',
    entityDescription: 'GMP Certification CERT-GMP-2024-04 approved',
    ipAddress: '10.0.2.11', sessionId: sid(),
  },
  {
    id: 'a025', timestamp: ts(d => subDays(d, 50)),
    user: 'Meena Iyer', role: 'Regulatory Affairs',
    module: 'Certification', action: 'CREATE',
    entityCode: 'CERT-ISO-2024-02',
    entityDescription: 'ISO 17025 renewal certification record created',
    ipAddress: '10.0.3.20', sessionId: sid(),
  },
  {
    id: 'a026', timestamp: ts(d => subDays(d, 48)),
    user: 'Meena Iyer', role: 'Regulatory Affairs',
    module: 'Certification', action: 'EXPORT',
    entityCode: 'CERT-ISO-2024-02',
    entityDescription: 'Exported CERT-ISO-2024-02 for accreditation body submission',
    ipAddress: '10.0.3.20', sessionId: sid(),
  },

  // ── Change Control ────────────────────────────────────────────────────────
  {
    id: 'a027', timestamp: ts(d => subDays(d, 85)),
    user: 'Arun Menon', role: 'Production Supervisor',
    module: 'Change Control', action: 'CREATE',
    entityCode: 'CHG-2024-031',
    entityDescription: 'Change request CHG-2024-031: Granulator speed parameter change',
    ipAddress: '10.0.4.12', sessionId: sid(),
  },
  {
    id: 'a028', timestamp: ts(d => subDays(d, 82)),
    user: 'Dr. Kavita Lal', role: 'QA Manager',
    module: 'Change Control', action: 'REVIEW',
    entityCode: 'CHG-2024-031',
    entityDescription: 'QA review of change request CHG-2024-031',
    ipAddress: '10.0.1.42', sessionId: sid(),
  },
  {
    id: 'a029', timestamp: ts(d => subDays(d, 79)),
    user: 'Dr. Anjali Mehta', role: 'QA Head',
    module: 'Change Control', action: 'APPROVE',
    entityCode: 'CHG-2024-031',
    entityDescription: 'Approved CHG-2024-031 with implementation plan',
    ipAddress: '10.0.2.11', sessionId: sid(),
  },
  {
    id: 'a030', timestamp: ts(d => subDays(d, 40)),
    user: 'Harish Reddy', role: 'Document Controller',
    module: 'Change Control', action: 'UPDATE',
    entityCode: 'CHG-2024-035',
    entityDescription: 'Updated implementation date for CHG-2024-035',
    previousValue: 'Implementation: 2024-11-01',
    newValue: 'Implementation: 2024-10-15',
    ipAddress: '10.0.1.34', sessionId: sid(),
  },
  {
    id: 'a031', timestamp: ts(d => subDays(d, 20)),
    user: 'Dr. Kavita Lal', role: 'QA Manager',
    module: 'Change Control', action: 'REJECT',
    entityCode: 'CHG-2024-038',
    entityDescription: 'CHG-2024-038 rejected — risk assessment incomplete',
    ipAddress: '10.0.1.42', sessionId: sid(),
  },

  // ── Training ──────────────────────────────────────────────────────────────
  {
    id: 'a032', timestamp: ts(d => subDays(d, 62)),
    user: 'Suresh Nair', role: 'Training Coordinator',
    module: 'Training', action: 'CREATE',
    entityCode: 'TRN-2024-GMP-08',
    entityDescription: 'Training record TRN-2024-GMP-08 created for new analyst batch',
    ipAddress: '10.0.5.18', sessionId: sid(),
  },
  {
    id: 'a033', timestamp: ts(d => subDays(d, 58)),
    user: 'Suresh Nair', role: 'Training Coordinator',
    module: 'Training', action: 'UPDATE',
    entityCode: 'TRN-2024-GMP-08',
    entityDescription: 'Updated trainer for TRN-2024-GMP-08',
    previousValue: 'Trainer: Dr. Anita Rao',
    newValue: 'Trainer: Dr. Kavita Lal',
    ipAddress: '10.0.5.18', sessionId: sid(),
  },
  {
    id: 'a034', timestamp: ts(d => subDays(d, 33)),
    user: 'Dr. Kavita Lal', role: 'QA Manager',
    module: 'Training', action: 'APPROVE',
    entityCode: 'TRN-2024-HPLC-03',
    entityDescription: 'HPLC training completion approved for 4 analysts',
    ipAddress: '10.0.1.42', sessionId: sid(),
  },
  {
    id: 'a035', timestamp: ts(d => subDays(d, 15)),
    user: 'Suresh Nair', role: 'Training Coordinator',
    module: 'Training', action: 'EXPORT',
    entityCode: 'TRN-Q3-2024-MATRIX',
    entityDescription: 'Q3 2024 training matrix exported for regulatory review',
    ipAddress: '10.0.5.18', sessionId: sid(),
  },

  // ── Audit ─────────────────────────────────────────────────────────────────
  {
    id: 'a036', timestamp: ts(d => subDays(d, 90)),
    user: 'System', role: 'Automated',
    module: 'Audit', action: 'CREATE',
    entityCode: 'AUD-2024-002',
    entityDescription: 'Internal GMP audit AUD-2024-002 scheduled',
    ipAddress: '127.0.0.1', sessionId: sid(),
  },
  {
    id: 'a037', timestamp: ts(d => subDays(d, 65)),
    user: 'Dr. Anjali Mehta', role: 'QA Head',
    module: 'Audit', action: 'REVIEW',
    entityCode: 'AUD-2024-002',
    entityDescription: 'Pre-audit checklist review for AUD-2024-002',
    ipAddress: '10.0.2.11', sessionId: sid(),
  },
  {
    id: 'a038', timestamp: ts(d => subDays(d, 44)),
    user: 'Dr. Anjali Mehta', role: 'QA Head',
    module: 'Audit', action: 'APPROVE',
    entityCode: 'AUD-2024-002',
    entityDescription: 'Audit report AUD-2024-002 approved and distributed',
    ipAddress: '10.0.2.11', sessionId: sid(),
  },
  {
    id: 'a039', timestamp: ts(d => subDays(d, 28)),
    user: 'Harish Reddy', role: 'Document Controller',
    module: 'Audit', action: 'EXPORT',
    entityCode: 'AUD-2024-002',
    entityDescription: 'Audit report AUD-2024-002 exported for CDSCO file',
    ipAddress: '10.0.1.34', sessionId: sid(),
  },

  // ── User Management ───────────────────────────────────────────────────────
  {
    id: 'a040', timestamp: ts(d => subDays(d, 75)),
    user: 'Admin', role: 'System Administrator',
    module: 'User Management', action: 'CREATE',
    entityCode: 'USR-2024-0042',
    entityDescription: 'New user account created for Anita Rao (Lab Analyst)',
    ipAddress: '10.0.0.5', sessionId: sid(),
  },
  {
    id: 'a041', timestamp: ts(d => subDays(d, 72)),
    user: 'Admin', role: 'System Administrator',
    module: 'User Management', action: 'UPDATE',
    entityCode: 'USR-2024-0039',
    entityDescription: 'Role updated for Ramesh Kulkarni',
    previousValue: 'Role: Lab Analyst',
    newValue: 'Role: QC Analyst',
    ipAddress: '10.0.0.5', sessionId: sid(),
  },
  {
    id: 'a042', timestamp: ts(d => subDays(d, 50)),
    user: 'Admin', role: 'System Administrator',
    module: 'User Management', action: 'DELETE',
    entityCode: 'USR-2024-0028',
    entityDescription: 'Deactivated account for departed staff member (ID USR-2024-0028)',
    ipAddress: '10.0.0.5', sessionId: sid(),
  },
  {
    id: 'a043', timestamp: ts(d => subDays(d, 25)),
    user: 'Admin', role: 'System Administrator',
    module: 'User Management', action: 'UPDATE',
    entityCode: 'USR-2024-0042',
    entityDescription: 'Password reset for Anita Rao per policy',
    previousValue: 'Password last changed: 2024-04-01',
    newValue: 'Password reset: system-enforced rotation',
    ipAddress: '10.0.0.5', sessionId: sid(),
  },

  // ── Settings ──────────────────────────────────────────────────────────────
  {
    id: 'a044', timestamp: ts(d => subDays(d, 88)),
    user: 'Admin', role: 'System Administrator',
    module: 'Settings', action: 'UPDATE',
    entityCode: 'CFG-SESSION-TIMEOUT',
    entityDescription: 'Session timeout policy updated',
    previousValue: 'Timeout: 30 minutes',
    newValue: 'Timeout: 20 minutes',
    ipAddress: '10.0.0.5', sessionId: sid(),
  },
  {
    id: 'a045', timestamp: ts(d => subDays(d, 77)),
    user: 'Admin', role: 'System Administrator',
    module: 'Settings', action: 'UPDATE',
    entityCode: 'CFG-PASSWORD-POLICY',
    entityDescription: 'Password complexity policy updated to 21 CFR Part 11 requirements',
    previousValue: 'Min length: 8, complexity: basic',
    newValue: 'Min length: 12, complexity: high, MFA enabled',
    ipAddress: '10.0.0.5', sessionId: sid(),
  },
  {
    id: 'a046', timestamp: ts(d => subDays(d, 30)),
    user: 'Admin', role: 'System Administrator',
    module: 'Settings', action: 'UPDATE',
    entityCode: 'CFG-AUDIT-RETENTION',
    entityDescription: 'Audit trail retention period configured',
    previousValue: 'Retention: 3 years',
    newValue: 'Retention: 7 years (regulatory compliance)',
    ipAddress: '10.0.0.5', sessionId: sid(),
  },

  // ── Login / Logout ────────────────────────────────────────────────────────
  {
    id: 'a047', timestamp: ts(d => subMinutes(d, 30)),
    user: 'Dr. Kavita Lal', role: 'QA Manager',
    module: 'Login', action: 'LOGIN',
    entityCode: 'SESSION',
    entityDescription: 'User logged in successfully',
    ipAddress: '10.0.1.42', sessionId: sid(),
  },
  {
    id: 'a048', timestamp: ts(d => subHours(d, 1)),
    user: 'Priya Sharma', role: 'QC Senior Analyst',
    module: 'Login', action: 'LOGIN',
    entityCode: 'SESSION',
    entityDescription: 'User logged in successfully',
    ipAddress: '10.0.1.77', sessionId: sid(),
  },
  {
    id: 'a049', timestamp: ts(d => subHours(d, 2)),
    user: 'Harish Reddy', role: 'Document Controller',
    module: 'Login', action: 'LOGIN',
    entityCode: 'SESSION',
    entityDescription: 'User logged in successfully',
    ipAddress: '10.0.1.34', sessionId: sid(),
  },
  {
    id: 'a050', timestamp: ts(d => subHours(d, 5)),
    user: 'Anita Rao', role: 'Lab Analyst',
    module: 'Login', action: 'LOGOUT',
    entityCode: 'SESSION',
    entityDescription: 'User session ended',
    ipAddress: '10.0.1.90', sessionId: sid(),
  },
  {
    id: 'a051', timestamp: ts(d => subHours(d, 8)),
    user: 'Arun Menon', role: 'Production Supervisor',
    module: 'Login', action: 'LOGIN',
    entityCode: 'SESSION',
    entityDescription: 'User logged in successfully',
    ipAddress: '10.0.4.12', sessionId: sid(),
  },
  {
    id: 'a052', timestamp: ts(d => subDays(d, 1)),
    user: 'Ramesh Kulkarni', role: 'QC Analyst',
    module: 'Login', action: 'LOGIN',
    entityCode: 'SESSION',
    entityDescription: 'User logged in successfully',
    ipAddress: '10.0.1.55', sessionId: sid(),
  },
  {
    id: 'a053', timestamp: ts(d => subDays(d, 1)),
    user: 'Ramesh Kulkarni', role: 'QC Analyst',
    module: 'Login', action: 'LOGOUT',
    entityCode: 'SESSION',
    entityDescription: 'User session ended',
    ipAddress: '10.0.1.55', sessionId: sid(),
  },
  {
    id: 'a054', timestamp: ts(d => subDays(d, 2)),
    user: 'Meena Iyer', role: 'Regulatory Affairs',
    module: 'Login', action: 'LOGIN',
    entityCode: 'SESSION',
    entityDescription: 'User logged in successfully from new device',
    ipAddress: '192.168.10.44', sessionId: sid(),
  },
  {
    id: 'a055', timestamp: ts(d => subDays(d, 3)),
    user: 'Suresh Nair', role: 'Training Coordinator',
    module: 'Login', action: 'LOGIN',
    entityCode: 'SESSION',
    entityDescription: 'User logged in successfully',
    ipAddress: '10.0.5.18', sessionId: sid(),
  },
  {
    id: 'a056', timestamp: ts(d => subDays(d, 4)),
    user: 'Admin', role: 'System Administrator',
    module: 'Login', action: 'LOGIN',
    entityCode: 'SESSION',
    entityDescription: 'Administrator login',
    ipAddress: '10.0.0.5', sessionId: sid(),
  },

  // ── Additional CAPA / SOP / Batch / Sample events ─────────────────────────
  {
    id: 'a057', timestamp: ts(d => subDays(d, 35)),
    user: 'Priya Sharma', role: 'QC Senior Analyst',
    module: 'CAPA', action: 'CREATE',
    entityCode: 'CAPA-2024-020',
    entityDescription: 'CAPA-2024-020 opened — Environmental Monitoring Exceedance',
    ipAddress: '10.0.1.77', sessionId: sid(),
  },
  {
    id: 'a058', timestamp: ts(d => subDays(d, 32)),
    user: 'Dr. Kavita Lal', role: 'QA Manager',
    module: 'CAPA', action: 'REVIEW',
    entityCode: 'CAPA-2024-020',
    entityDescription: 'QA review of CAPA-2024-020 root cause analysis',
    ipAddress: '10.0.1.42', sessionId: sid(),
  },
  {
    id: 'a059', timestamp: ts(d => subDays(d, 16)),
    user: 'Anita Rao', role: 'Lab Analyst',
    module: 'Sample', action: 'CREATE',
    entityCode: 'SMP-2024-1002',
    entityDescription: 'New stability sample SMP-2024-1002 logged — Metformin T=3M',
    ipAddress: '10.0.1.90', sessionId: sid(),
  },
  {
    id: 'a060', timestamp: ts(d => subDays(d, 11)),
    user: 'Priya Sharma', role: 'QC Senior Analyst',
    module: 'Sample', action: 'REVIEW',
    entityCode: 'SMP-2024-0988',
    entityDescription: 'Stability sample SMP-2024-0988 results reviewed',
    ipAddress: '10.0.1.77', sessionId: sid(),
  },
  {
    id: 'a061', timestamp: ts(d => subDays(d, 9)),
    user: 'Ramesh Kulkarni', role: 'QC Analyst',
    module: 'Batch Record', action: 'UPDATE',
    entityCode: 'BT-MET-24012',
    entityDescription: 'Environmental condition recorded for BT-MET-24012',
    previousValue: 'Humidity: not recorded',
    newValue: 'Humidity: 42% RH (within 30–60% limit)',
    ipAddress: '10.0.1.55', sessionId: sid(),
  },
  {
    id: 'a062', timestamp: ts(d => subDays(d, 6)),
    user: 'Dr. Anjali Mehta', role: 'QA Head',
    module: 'Batch Record', action: 'APPROVE',
    entityCode: 'BT-MET-24012',
    entityDescription: 'Batch record BT-MET-24012 approved and locked',
    ipAddress: '10.0.2.11', sessionId: sid(),
  },
  {
    id: 'a063', timestamp: ts(d => subDays(d, 8)),
    user: 'Harish Reddy', role: 'Document Controller',
    module: 'SOP', action: 'UPDATE',
    entityCode: 'SOP-MFG-045',
    entityDescription: 'SOP-MFG-045 revised to include IPC checkpoints',
    previousValue: 'Version: 1.0',
    newValue: 'Version: 1.1',
    ipAddress: '10.0.1.34', sessionId: sid(),
  },
  {
    id: 'a064', timestamp: ts(d => subDays(d, 7)),
    user: 'Dr. Kavita Lal', role: 'QA Manager',
    module: 'SOP', action: 'APPROVE',
    entityCode: 'SOP-MFG-045',
    entityDescription: 'Approved SOP-MFG-045 v1.1 — Granulation Process Control',
    ipAddress: '10.0.1.42', sessionId: sid(),
  },
  {
    id: 'a065', timestamp: ts(d => subDays(d, 4)),
    user: 'Arun Menon', role: 'Production Supervisor',
    module: 'Change Control', action: 'CREATE',
    entityCode: 'CHG-2024-044',
    entityDescription: 'Change request CHG-2024-044: API supplier qualification',
    ipAddress: '10.0.4.12', sessionId: sid(),
  },
  {
    id: 'a066', timestamp: ts(d => subDays(d, 3)),
    user: 'Meena Iyer', role: 'Regulatory Affairs',
    module: 'Change Control', action: 'REVIEW',
    entityCode: 'CHG-2024-044',
    entityDescription: 'Regulatory impact assessment for CHG-2024-044',
    ipAddress: '10.0.3.20', sessionId: sid(),
  },
  {
    id: 'a067', timestamp: ts(d => subDays(d, 13)),
    user: 'Suresh Nair', role: 'Training Coordinator',
    module: 'Training', action: 'CREATE',
    entityCode: 'TRN-2024-ASP-05',
    entityDescription: 'Aseptic technique training TRN-2024-ASP-05 created',
    ipAddress: '10.0.5.18', sessionId: sid(),
  },
  {
    id: 'a068', timestamp: ts(d => subDays(d, 2)),
    user: 'Anita Rao', role: 'Lab Analyst',
    module: 'Training', action: 'REVIEW',
    entityCode: 'TRN-2024-ASP-05',
    entityDescription: 'Aseptic training assessment reviewed for Anita Rao',
    ipAddress: '10.0.1.90', sessionId: sid(),
  },
  {
    id: 'a069', timestamp: ts(d => subHours(d, 10)),
    user: 'Meena Iyer', role: 'Regulatory Affairs',
    module: 'Certification', action: 'UPDATE',
    entityCode: 'CERT-ISO-2024-02',
    entityDescription: 'Updated ISO 17025 scope of accreditation',
    previousValue: 'Scope: chemical testing only',
    newValue: 'Scope: chemical + microbiological testing',
    ipAddress: '10.0.3.20', sessionId: sid(),
  },
  {
    id: 'a070', timestamp: ts(d => subHours(d, 4)),
    user: 'Dr. Anjali Mehta', role: 'QA Head',
    module: 'Document', action: 'REVIEW',
    entityCode: 'DOC-VAL-2024-009',
    entityDescription: 'Reviewed equipment qualification protocol DOC-VAL-2024-009',
    ipAddress: '10.0.2.11', sessionId: sid(),
  },
  {
    id: 'a071', timestamp: ts(d => subMinutes(d, 90)),
    user: 'Priya Sharma', role: 'QC Senior Analyst',
    module: 'Sample', action: 'EXPORT',
    entityCode: 'SMP-Q3-2024-REPORT',
    entityDescription: 'Q3 2024 OOS summary report exported',
    ipAddress: '10.0.1.77', sessionId: sid(),
  },
  {
    id: 'a072', timestamp: ts(d => subMinutes(d, 45)),
    user: 'Harish Reddy', role: 'Document Controller',
    module: 'SOP', action: 'EXPORT',
    entityCode: 'SOP-MASTER-INDEX',
    entityDescription: 'Master SOP index exported for external audit preparation',
    ipAddress: '10.0.1.34', sessionId: sid(),
  },
  {
    id: 'a073', timestamp: ts(d => subDays(d, 17)),
    user: 'System', role: 'Automated',
    module: 'Batch Record', action: 'CREATE',
    entityCode: 'BT-PAR-24015',
    entityDescription: 'Batch BT-PAR-24015 auto-created from dispensing workflow',
    ipAddress: '127.0.0.1', sessionId: sid(),
  },
  {
    id: 'a074', timestamp: ts(d => subDays(d, 27)),
    user: 'Ramesh Kulkarni', role: 'QC Analyst',
    module: 'CAPA', action: 'UPDATE',
    entityCode: 'CAPA-2024-018',
    entityDescription: 'Effectiveness check results added to CAPA-2024-018',
    previousValue: 'Effectiveness: pending',
    newValue: 'Effectiveness: verified — no recurrence in 90 days',
    ipAddress: '10.0.1.55', sessionId: sid(),
  },
  {
    id: 'a075', timestamp: ts(d => subDays(d, 24)),
    user: 'Dr. Kavita Lal', role: 'QA Manager',
    module: 'CAPA', action: 'APPROVE',
    entityCode: 'CAPA-2024-018',
    entityDescription: 'CAPA-2024-018 closed after successful effectiveness verification',
    ipAddress: '10.0.1.42', sessionId: sid(),
  },
  {
    id: 'a076', timestamp: ts(d => subDays(d, 19)),
    user: 'Admin', role: 'System Administrator',
    module: 'Settings', action: 'UPDATE',
    entityCode: 'CFG-EMAIL-ALERTS',
    entityDescription: 'Email notification thresholds updated',
    previousValue: 'CAPA overdue alert: 7 days',
    newValue: 'CAPA overdue alert: 3 days',
    ipAddress: '10.0.0.5', sessionId: sid(),
  },
  {
    id: 'a077', timestamp: ts(d => subDays(d, 42)),
    user: 'Arun Menon', role: 'Production Supervisor',
    module: 'Batch Record', action: 'EXPORT',
    entityCode: 'BT-AMX-24001',
    entityDescription: 'Batch record BT-AMX-24001 exported for QA archive',
    ipAddress: '10.0.4.12', sessionId: sid(),
  },
  {
    id: 'a078', timestamp: ts(d => subDays(d, 53)),
    user: 'Dr. Anjali Mehta', role: 'QA Head',
    module: 'Change Control', action: 'APPROVE',
    entityCode: 'CHG-2024-035',
    entityDescription: 'CHG-2024-035 approved — packaging line modification',
    ipAddress: '10.0.2.11', sessionId: sid(),
  },
  {
    id: 'a079', timestamp: ts(d => subHours(d, 12)),
    user: 'Suresh Nair', role: 'Training Coordinator',
    module: 'Training', action: 'UPDATE',
    entityCode: 'TRN-2024-GMP-08',
    entityDescription: 'Assessment scores entered for TRN-2024-GMP-08 participants',
    previousValue: 'Scores: pending',
    newValue: 'Scores: entered (avg 87.3%)',
    ipAddress: '10.0.5.18', sessionId: sid(),
  },
  {
    id: 'a080', timestamp: ts(d => subMinutes(d, 15)),
    user: 'Dr. Kavita Lal', role: 'QA Manager',
    module: 'Audit', action: 'CREATE',
    entityCode: 'AUD-2024-003',
    entityDescription: 'Supplier audit AUD-2024-003 scheduled for Q4 review',
    ipAddress: '10.0.1.42', sessionId: sid(),
  },
];

// ─── Constants ─────────────────────────────────────────────────────────────────

const MODULES = [
  'All Modules', 'CAPA', 'Audit', 'SOP', 'Batch Record', 'Certification',
  'Document', 'Sample', 'Training', 'Change Control', 'User Management',
  'Settings', 'Login',
];

const ACTIONS = [
  'All Actions', 'CREATE', 'UPDATE', 'DELETE', 'LOGIN', 'LOGOUT',
  'EXPORT', 'APPROVE', 'REJECT', 'REVIEW',
];

const RANGES = [
  { label: 'Last 24 hours', value: '24h' },
  { label: 'Last 7 days', value: '7d' },
  { label: 'Last 30 days', value: '30d' },
  { label: 'Last 90 days', value: '90d' },
];

const ACTION_BADGE: Record<AuditEntry['action'], string> = {
  CREATE:  'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  UPDATE:  'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  DELETE:  'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  LOGIN:   'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  LOGOUT:  'bg-slate-100 text-slate-600 dark:bg-slate-700/40 dark:text-slate-300',
  EXPORT:  'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
  APPROVE: 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300',
  REJECT:  'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300',
  REVIEW:  'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300',
};

const CRITICAL_ACTIONS: AuditEntry['action'][] = ['DELETE', 'APPROVE', 'REJECT'];

// ─── Page ──────────────────────────────────────────────────────────────────────

export default function AuditTrail() {
  const [search, setSearch]       = useState('');
  const [module, setModule]       = useState('All Modules');
  const [action, setAction]       = useState('All Actions');
  const [range, setRange]         = useState('30d');
  const [page, setPage]           = useState(1);
  const [selected, setSelected]   = useState<AuditEntry | null>(null);

  const PAGE_SIZE = 20;

  // ── API call (falls back to mock on error) ──────────────────────────────
  const { data: apiData, isError } = useQuery({
    queryKey: ['audit-trail', search, module, action, range, page],
    queryFn: async () => {
      const res = await api.get('/audit-trail', {
        params: { search, module, action, range, page, limit: PAGE_SIZE },
      });
      return res.data as { data: AuditEntry[]; meta: { total: number } };
    },
    retry: false,
  });

  // ── Client-side filtering over mock data ────────────────────────────────
  const rangeMs: Record<string, number> = {
    '24h': 24 * 60 * 60 * 1000,
    '7d':  7  * 24 * 60 * 60 * 1000,
    '30d': 30 * 24 * 60 * 60 * 1000,
    '90d': 90 * 24 * 60 * 60 * 1000,
  };

  const filtered = useMemo(() => {
    if (apiData && !isError) return apiData.data;
    const cutoff = Date.now() - (rangeMs[range] ?? rangeMs['30d']);
    return MOCK_AUDIT.filter(e => {
      const tMs = new Date(e.timestamp).getTime();
      if (tMs < cutoff) return false;
      if (module !== 'All Modules' && e.module !== module) return false;
      if (action !== 'All Actions' && e.action !== action) return false;
      if (search) {
        const q = search.toLowerCase();
        if (
          !e.user.toLowerCase().includes(q) &&
          !e.module.toLowerCase().includes(q) &&
          !e.entityCode.toLowerCase().includes(q) &&
          !e.entityDescription.toLowerCase().includes(q)
        ) return false;
      }
      return true;
    }).sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  }, [search, module, action, range, apiData, isError]);

  const total = apiData && !isError ? apiData.meta.total : filtered.length;
  const paginated = apiData && !isError
    ? filtered
    : filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // ── Stats ────────────────────────────────────────────────────────────────
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const stats = useMemo(() => {
    const todayEntries  = MOCK_AUDIT.filter(e => new Date(e.timestamp) >= today);
    const usersToday    = new Set(todayEntries.map(e => e.user)).size;
    const criticalCount = MOCK_AUDIT.filter(e => CRITICAL_ACTIONS.includes(e.action)).length;
    return { total: MOCK_AUDIT.length, todayCount: todayEntries.length, usersToday, criticalCount };
  }, []);

  // ── Export ───────────────────────────────────────────────────────────────
  const exportColumns = [
    { key: 'timestamp',         label: 'Timestamp' },
    { key: 'user',              label: 'User' },
    { key: 'role',              label: 'Role' },
    { key: 'module',            label: 'Module' },
    { key: 'action',            label: 'Action' },
    { key: 'entityCode',        label: 'Entity Code' },
    { key: 'entityDescription', label: 'Description' },
    { key: 'ipAddress',         label: 'IP Address' },
    { key: 'sessionId',         label: 'Session ID' },
  ];

  const { exportCSV, exportPDF } = useTableExport(
    filtered,
    exportColumns,
    'audit-trail'
  );

  // ── Table columns ────────────────────────────────────────────────────────
  const columns: Column<AuditEntry>[] = [
    {
      key: 'timestamp',
      label: 'Timestamp',
      render: (row) => {
        let display = '—';
        try {
          const d = new Date(row.timestamp);
          if (!isNaN(d.getTime())) display = format(d, 'd MMM yyyy HH:mm:ss');
        } catch { /* noop */ }
        return <span className="font-mono text-xs text-text">{display}</span>;
      },
    },
    {
      key: 'user',
      label: 'User / Role',
      render: (row) => (
        <div>
          <p className="text-sm font-medium text-text leading-tight">{row.user}</p>
          <p className="text-xs text-text-muted">{row.role}</p>
        </div>
      ),
    },
    {
      key: 'module',
      label: 'Module',
      render: (row) => (
        <span className="text-sm text-text">{row.module}</span>
      ),
    },
    {
      key: 'action',
      label: 'Action',
      render: (row) => (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${ACTION_BADGE[row.action]}`}>
          {row.action}
        </span>
      ),
    },
    {
      key: 'entityDescription',
      label: 'Entity / Record',
      render: (row) => (
        <div>
          <p className="text-xs font-mono text-text-muted">{row.entityCode}</p>
          <p className="text-sm text-text line-clamp-1">{row.entityDescription}</p>
        </div>
      ),
    },
    {
      key: 'ipAddress',
      label: 'IP Address',
      render: (row) => (
        <span className="font-mono text-xs text-text-muted">{row.ipAddress}</span>
      ),
    },
  ];

  return (
    <div className="p-6 space-y-6">
      {/* ── Page Header ───────────────────────────────────────────────────── */}
      <PageHeader
        title="Audit Trail"
        subtitle="M09 · Immutable system-wide activity log"
        actions={
          <>
            <button onClick={exportCSV} className="btn-ghost flex items-center gap-2 text-sm">
              <Download size={15} />
              Export CSV
            </button>
            <button onClick={exportPDF} className="btn-ghost flex items-center gap-2 text-sm">
              <FileText size={15} />
              Export PDF
            </button>
          </>
        }
      />

      {/* ── Compliance Banner ─────────────────────────────────────────────── */}
      <div className="flex items-start gap-3 px-4 py-3 rounded-lg border border-teal-300 bg-teal-50 dark:bg-teal-900/20 dark:border-teal-700">
        <ShieldCheck size={18} className="text-teal-600 dark:text-teal-400 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-teal-800 dark:text-teal-300 font-medium">
          21 CFR Part 11 compliant — all entries are immutable, timestamped, and user-attributed.
          Audit log cannot be modified or deleted.
        </p>
      </div>

      {/* ── Stats ─────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <Activity size={18} className="text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-text">{stats.total}</p>
              <p className="text-xs text-text-muted">Total Events</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <Clock size={18} className="text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-text">{stats.todayCount}</p>
              <p className="text-xs text-text-muted">Today's Events</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
              <Users size={18} className="text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-text">{stats.usersToday}</p>
              <p className="text-xs text-text-muted">Users Active Today</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <AlertOctagon size={18} className="text-red-600 dark:text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-text">{stats.criticalCount}</p>
              <p className="text-xs text-text-muted">Critical Actions</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Filters ───────────────────────────────────────────────────────── */}
      <div className="card p-4">
        <div className="flex flex-wrap gap-3">
          {/* Search */}
          <div className="relative flex-1 min-w-[220px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-dim" />
            <input
              className="input pl-9 w-full text-sm"
              placeholder="Search user, module, entity…"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
            />
          </div>

          {/* Module */}
          <select
            className="select text-sm min-w-[160px]"
            value={module}
            onChange={e => { setModule(e.target.value); setPage(1); }}
          >
            {MODULES.map(m => <option key={m}>{m}</option>)}
          </select>

          {/* Action */}
          <select
            className="select text-sm min-w-[140px]"
            value={action}
            onChange={e => { setAction(e.target.value); setPage(1); }}
          >
            {ACTIONS.map(a => <option key={a}>{a}</option>)}
          </select>

          {/* Date Range */}
          <select
            className="select text-sm min-w-[150px]"
            value={range}
            onChange={e => { setRange(e.target.value); setPage(1); }}
          >
            {RANGES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>
      </div>

      {/* ── DataTable ─────────────────────────────────────────────────────── */}
      <DataTable<AuditEntry>
        columns={columns}
        data={paginated}
        keyField="id"
        onRowClick={setSelected}
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onPageChange={setPage}
        emptyMessage="No audit events match your filters."
        tableId="audit-trail-table"
      />

      {/* ── Side Drawer ───────────────────────────────────────────────────── */}
      <SideDrawer
        isOpen={!!selected}
        onClose={() => setSelected(null)}
        title="Audit Event Detail"
        subtitle={selected ? `${selected.entityCode} · ${selected.module}` : undefined}
        width={520}
      >
        {selected && (
          <div className="space-y-4 text-sm">
            {/* Compliance note */}
            <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-700">
              <ShieldCheck size={14} className="text-teal-600 dark:text-teal-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-teal-700 dark:text-teal-300">
                This record is immutable and cryptographically timestamped in compliance with 21 CFR Part 11.
              </p>
            </div>

            {/* Details grid */}
            <div className="space-y-3">
              <Row label="Timestamp">
                <span className="font-mono text-xs">
                  {(() => {
                    try {
                      const d = new Date(selected.timestamp);
                      return !isNaN(d.getTime()) ? format(d, "d MMM yyyy HH:mm:ss 'UTC'xxx") : '—';
                    } catch { return '—'; }
                  })()}
                </span>
              </Row>
              <Row label="User">{selected.user}</Row>
              <Row label="Role">{selected.role}</Row>
              <Row label="Module">{selected.module}</Row>
              <Row label="Action">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${ACTION_BADGE[selected.action]}`}>
                  {selected.action}
                </span>
              </Row>
              <Row label="Entity Code">
                <span className="font-mono text-xs">{selected.entityCode}</span>
              </Row>
              <Row label="Description">{selected.entityDescription}</Row>

              {selected.action === 'UPDATE' && selected.previousValue && (
                <Row label="Previous Value">
                  <span className="text-red-600 dark:text-red-400 text-xs">{selected.previousValue}</span>
                </Row>
              )}
              {selected.action === 'UPDATE' && selected.newValue && (
                <Row label="New Value">
                  <span className="text-green-600 dark:text-green-400 text-xs">{selected.newValue}</span>
                </Row>
              )}

              <Row label="IP Address">
                <span className="font-mono text-xs">{selected.ipAddress}</span>
              </Row>
              <Row label="Session ID">
                <span className="font-mono text-xs">{selected.sessionId}</span>
              </Row>
            </div>
          </div>
        )}
      </SideDrawer>
    </div>
  );
}

// ─── Row helper ────────────────────────────────────────────────────────────────

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-3">
      <span className="text-text-muted w-32 flex-shrink-0 pt-0.5">{label}</span>
      <span className="text-text flex-1">{children}</span>
    </div>
  );
}
